/**
 * SpatialGoalEditor - Visual UI for configuring spatial goals on annotations
 *
 * Allows coaches to define distance-based constraints by clicking on video
 * to select source and target body landmarks visually.
 *
 * Features:
 * - Visual landmark picker overlay (replaces dropdown selection)
 * - Click-to-select source and target points
 * - Live preview of connection line
 * - Comparison mode and tolerance settings
 */

import { useState, useCallback } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  CursorArrowRaysIcon,
  CheckCircleIcon,
  ArrowsRightLeftIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { getLandmarkLabel, type PoseData } from '@/utils/landmarkDetection';
import type { SpatialGoal, SpatialTarget, SpatialObjective } from '@/types/api';

const OBJECTIVE_OPTIONS = [
  { value: 'match_template', label: 'Match Template', description: 'Distance should match template ±tolerance' },
  { value: 'less_or_equal', label: 'Less Than', description: 'Distance should be at or below template' },
  { value: 'greater_or_equal', label: 'Greater Than', description: 'Distance should be at or above template' },
  { value: 'minimize', label: 'Minimize', description: 'Track and aim for smallest distance' },
  { value: 'maximize', label: 'Maximize', description: 'Track and aim for largest distance' },
];

interface SpatialGoalEditorProps {
  value: SpatialGoal | undefined;
  onChange: (goal: SpatialGoal | undefined) => void;
  className?: string;
  hasPhases?: boolean;
  // Props for visual landmark selection
  poseData?: PoseData | null;
  videoWidth?: number;
  videoHeight?: number;
  videoContainerRef?: React.RefObject<HTMLDivElement | null>;
  // Callbacks to request landmark selection from parent (RegionAnnotator)
  onRequestSourceSelection?: (callback: (landmark: string) => void) => void;
  onRequestTargetSelection?: (callback: (landmark: string) => void) => void;
}

export default function SpatialGoalEditor({
  value,
  onChange,
  className,
  hasPhases: _hasPhases = true,
  poseData,
  videoWidth: _videoWidth = 1920,
  videoHeight: _videoHeight = 1080,
  videoContainerRef,
  onRequestSourceSelection,
  onRequestTargetSelection,
}: SpatialGoalEditorProps) {
  const [isExpanded, setIsExpanded] = useState(!!value?.enabled);

  const handleToggle = useCallback((enabled: boolean) => {
    if (enabled) {
      // Create default spatial goal without pre-selected landmarks
      // User will select them visually
      const defaultGoal: SpatialGoal = {
        enabled: true,
        source_landmark: '',
        target: {
          type: 'landmark',
          target_landmark: '',
        },
        objective: {
          mode: 'match_template',
          tolerance: { kind: 'percent', value: 15 },
        },
      };
      onChange(defaultGoal);
      setIsExpanded(true);
    } else {
      onChange(undefined);
      setIsExpanded(false);
    }
  }, [onChange]);

  const handleSourceSelect = useCallback((landmark: string) => {
    if (!value) return;
    onChange({
      ...value,
      source_landmark: landmark,
    });
  }, [value, onChange]);

  const handleTargetSelect = useCallback((landmark: string) => {
    if (!value) return;
    const newTarget: SpatialTarget = {
      type: 'landmark',
      target_landmark: landmark,
    };
    onChange({
      ...value,
      target: newTarget,
    });
  }, [value, onChange]);

  const handleObjectiveChange = useCallback((mode: SpatialObjective['mode']) => {
    if (!value) return;
    onChange({
      ...value,
      objective: {
        ...value.objective,
        mode,
      },
    });
  }, [value, onChange]);

  const handleToleranceChange = useCallback((toleranceValue: number) => {
    if (!value) return;
    onChange({
      ...value,
      objective: {
        mode: value.objective?.mode ?? 'match_template',
        ...value.objective,
        tolerance: { kind: 'percent', value: toleranceValue },
      },
    });
  }, [value, onChange]);

  const handleStartSourceSelection = useCallback(() => {
    if (!poseData) {
      console.warn('No pose data available for landmark selection');
      return;
    }
    if (onRequestSourceSelection) {
      onRequestSourceSelection(handleSourceSelect);
    }
  }, [poseData, onRequestSourceSelection, handleSourceSelect]);

  const handleStartTargetSelection = useCallback(() => {
    if (!poseData) {
      console.warn('No pose data available for landmark selection');
      return;
    }
    if (onRequestTargetSelection) {
      onRequestTargetSelection(handleTargetSelect);
    }
  }, [poseData, onRequestTargetSelection, handleTargetSelect]);

  // Check if visual selection is available
  const canUseVisualSelection = !!poseData && !!videoContainerRef?.current;
  const hasSource = !!value?.source_landmark;
  const hasTarget = !!value?.target?.target_landmark;
  const isComplete = hasSource && hasTarget;

  return (
    <div className={cn('border rounded-lg overflow-hidden', className)}>
      {/* Header */}
      <button
        type="button"
        onClick={() => {
          if (!value?.enabled) {
            handleToggle(true);
          } else {
            setIsExpanded(!isExpanded);
          }
        }}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors',
          value?.enabled
            ? 'bg-dark-elevated-2 hover:bg-dark-elevated-2 border-b border-border-subtle'
            : 'bg-dark-elevated-1 hover:bg-dark-elevated-2'
        )}
      >
        <div className="flex items-center gap-2.5">
          <input
            type="checkbox"
            checked={value?.enabled ?? false}
            onChange={(e) => {
              e.stopPropagation();
              handleToggle(e.target.checked);
            }}
            className="h-4 w-4 text-blue-600 rounded border-border-subtle focus:ring-blue-500"
          />
          <ArrowsRightLeftIcon className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold text-text-primary">
            Distance Constraint
          </span>
          {!value?.enabled && (
            <span className="text-xs text-text-muted font-normal">(click to enable)</span>
          )}
          {value?.enabled && isComplete && (
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
          )}
        </div>
        {value?.enabled && (
          isExpanded ? (
            <ChevronUpIcon className="h-4 w-4 text-text-secondary" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-text-secondary" />
          )
        )}
      </button>

      {/* Expanded Content */}
      {value?.enabled && isExpanded && (
        <div className="p-4 space-y-4 bg-dark-card">
          {/* Info Banner */}
          <div className="flex items-start gap-2.5 p-3 bg-info/10 rounded-lg text-xs text-info border border-info/30">
            <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-info" />
            <span>
              <strong>Distance Check:</strong> Define a constraint between two body points.
              Click the buttons below to select points visually on the video.
            </span>
          </div>

          {/* Visual Landmark Selection */}
          <div className="space-y-3">
            {/* Source Point */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1.5">
                  From Point
                </label>
                <button
                  type="button"
                  onClick={handleStartSourceSelection}
                  disabled={!canUseVisualSelection}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2.5 rounded-lg border-2 transition-all text-sm',
                    hasSource
                      ? 'bg-info/10 border-info/40 text-info'
                      : 'bg-dark-elevated-1 border-dashed border-border-subtle text-text-secondary hover:border-info/40 hover:bg-info/10',
                    !canUseVisualSelection && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-center gap-2">
                    {hasSource ? (
                      <>
                        <div className="w-3 h-3 rounded-full bg-blue-500 ring-2 ring-blue-500/30" />
                        <span className="font-medium">{getLandmarkLabel(value.source_landmark)}</span>
                      </>
                    ) : (
                      <>
                        <CursorArrowRaysIcon className="h-4 w-4" />
                        <span>Click to select source point</span>
                      </>
                    )}
                  </div>
                  {hasSource && (
                    <span className="text-xs text-info">Change</span>
                  )}
                </button>
              </div>
            </div>

            {/* Connection indicator */}
            <div className="flex justify-center">
              <div className={cn(
                'flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium',
                isComplete
                  ? 'bg-success/10 text-success'
                  : 'bg-dark-elevated-2 text-text-secondary'
              )}>
                <div className={cn(
                  'w-8 h-0.5 rounded',
                  isComplete ? 'bg-success' : 'bg-border-subtle'
                )} />
                <span>{isComplete ? 'Connected' : 'Select both points'}</span>
                <div className={cn(
                  'w-8 h-0.5 rounded',
                  isComplete ? 'bg-success' : 'bg-border-subtle'
                )} />
              </div>
            </div>

            {/* Target Point */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1.5">
                  To Point
                </label>
                <button
                  type="button"
                  onClick={handleStartTargetSelection}
                  disabled={!canUseVisualSelection}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2.5 rounded-lg border-2 transition-all text-sm',
                    hasTarget
                      ? 'bg-info/10 border-info/40 text-info'
                      : 'bg-dark-elevated-1 border-dashed border-border-subtle text-text-secondary hover:border-info/40 hover:bg-info/10',
                    !canUseVisualSelection && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-center gap-2">
                    {hasTarget ? (
                      <>
                        <div className="w-3 h-3 rounded-full bg-blue-500 ring-2 ring-blue-500/30" />
                        <span className="font-medium">{getLandmarkLabel(value.target.target_landmark!)}</span>
                      </>
                    ) : (
                      <>
                        <CursorArrowRaysIcon className="h-4 w-4" />
                        <span>Click to select target point</span>
                      </>
                    )}
                  </div>
                  {hasTarget && (
                    <span className="text-xs text-info">Change</span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* No pose data warning */}
          {!poseData && (
            <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg text-xs text-warning">
              <strong>Note:</strong> Pose data not loaded. Visual landmark selection will be available once pose data is extracted from the video.
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-border-subtle pt-4">
            <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">
              Comparison Settings
            </h4>

            {/* Objective Mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Comparison Mode
              </label>
              <select
                value={value.objective?.mode ?? 'match_template'}
                onChange={(e) => handleObjectiveChange(e.target.value as SpatialObjective['mode'])}
                className="w-full px-3 py-2 border border-border-subtle rounded-lg text-sm text-text-primary focus:ring-2 focus:ring-accent/50 focus:border-accent bg-dark-elevated-1"
              >
                {OBJECTIVE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-text-secondary">
                {OBJECTIVE_OPTIONS.find(o => o.value === (value.objective?.mode ?? 'match_template'))?.description}
              </p>
            </div>

            {/* Tolerance */}
            {(value.objective?.mode === 'match_template' || value.objective?.mode === 'less_or_equal' || value.objective?.mode === 'greater_or_equal') && (
              <div>
                <label className="flex items-center justify-between text-sm font-medium text-text-primary mb-2">
                  <span>Tolerance</span>
                  <span className="text-info font-semibold">±{value.objective?.tolerance?.value ?? 15}%</span>
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="5"
                  value={value.objective?.tolerance?.value ?? 15}
                  onChange={(e) => handleToleranceChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((value.objective?.tolerance?.value ?? 15) - 5) / 45 * 100}%, rgba(255,255,255,0.1) ${((value.objective?.tolerance?.value ?? 15) - 5) / 45 * 100}%, rgba(255,255,255,0.1) 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-text-muted mt-1">
                  <span>Strict (5%)</span>
                  <span>Lenient (50%)</span>
                </div>
              </div>
            )}

            {/* Gate Toggle - Required to Pass */}
            <div className="pt-3 border-t border-border-subtle">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldExclamationIcon className={cn(
                    'h-4 w-4',
                    value?.is_gate ? 'text-danger' : 'text-text-muted'
                  )} />
                  <label className="text-sm font-medium text-text-primary">
                    Required to Pass
                  </label>
                  <span className="text-xs text-text-muted">(Gate)</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={value?.is_gate ?? false}
                  onClick={() => {
                    if (value) {
                      onChange({ ...value, is_gate: !value.is_gate });
                    }
                  }}
                  className={cn(
                    'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-2',
                    value?.is_gate ? 'bg-danger' : 'bg-dark-elevated-2'
                  )}
                >
                  <span
                    className={cn(
                      'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                      value?.is_gate ? 'translate-x-4' : 'translate-x-0'
                    )}
                  />
                </button>
              </div>
              {value?.is_gate && (
                <p className="mt-2 text-xs text-danger bg-danger/10 px-2 py-1.5 rounded">
                  Athlete must pass this goal or receive automatic 0
                </p>
              )}
            </div>
          </div>

          {/* Summary */}
          {isComplete && (
            <div className="p-3 bg-success/10 rounded-lg border border-success/30">
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="h-5 w-5 text-success flex-shrink-0" />
                <div className="text-sm text-success">
                  <strong>Constraint Active:</strong> Distance from{' '}
                  <span className="font-semibold text-success">
                    {getLandmarkLabel(value.source_landmark)}
                  </span>
                  {' '}to{' '}
                  <span className="font-semibold text-success">
                    {getLandmarkLabel(value.target.target_landmark!)}
                  </span>
                  {' '}should{' '}
                  <span className="font-semibold">
                    {value.objective?.mode === 'match_template' && `match template ±${value.objective?.tolerance?.value ?? 15}%`}
                    {value.objective?.mode === 'less_or_equal' && 'be less than template'}
                    {value.objective?.mode === 'greater_or_equal' && 'be greater than template'}
                    {value.objective?.mode === 'minimize' && 'be minimized'}
                    {value.objective?.mode === 'maximize' && 'be maximized'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
